import React, { useEffect } from 'react'
import { useDebounce } from 'use-debounce'

import { Dashboard } from '@components/common/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { DashboardBackButton } from '@components/utils'
import { ButtonClean, HeadContent, Input, Slider } from '@fourthwall/components'
import { CloseIcon } from '@fourthwall/icons'
import { routing } from '@utils/routing'

import * as S from './AppsPledgeView.styled'

export type AppsPledgeViewProps = {
  publicKey: string
  currentCharity:
    | {
        charityName: string
        charityLogoUrl: string
        percentage?: number
      }
    | null
    | undefined
  clearCharity: () => void
  onPercentageChange?: (percentage: number) => void
}

export const AppsPledgeView = ({
  publicKey,
  currentCharity,
  clearCharity,
  onPercentageChange,
}: AppsPledgeViewProps) => {
  const [percentage, setPercentage] = React.useState(currentCharity?.percentage)
  const [percentageDebounced] = useDebounce(percentage, 1000)

  useEffect(() => {
    if (percentageDebounced) {
      onPercentageChange?.(percentageDebounced)
    }
  }, [percentageDebounced])

  const handlePercentageChange = (value: number) => {
    const clampedValue = Math.min(100, Math.max(0, value))
    setPercentage(clampedValue)
  }

  useEffect(() => {
    if (currentCharity?.percentage) {
      setPercentage(currentCharity.percentage)
    }
  }, [currentCharity?.percentage])

  return (
    <>
      <HeadContent title="Pledge" />
      <PageLayout size="tiny">
        <Dashboard.Section
          name="Pledge"
          title={
            <>
              Use Pledge to send a portion of your donation proceeds to a non-profit.{' '}
              <ButtonClean
                label="Learn More"
                href={routing.external.helpCenter.klaviyo}
                target="_blank"
              />
              .
            </>
          }
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
        >
          {currentCharity && (
            <>
              <S.CharityCard>
                <S.CharityLogo
                  src={currentCharity.charityLogoUrl}
                  alt={currentCharity.charityName}
                />
                <S.CharityContent>
                  <S.CharityName>{currentCharity.charityName}</S.CharityName>
                </S.CharityContent>
                <ButtonClean icon={CloseIcon} label="Clear" onClick={clearCharity} />
              </S.CharityCard>

              <S.PercentageSection>
                <S.PercentageHeader>
                  <S.PercentageLabel>Percentage Donated</S.PercentageLabel>
                  <S.PercentageInputGroup>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={percentage?.toString() ?? '0'}
                      onChange={(e) => handlePercentageChange(Number(e.target.value))}
                    />
                    <S.PercentageUnit>%</S.PercentageUnit>
                  </S.PercentageInputGroup>
                </S.PercentageHeader>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={percentage}
                  onChange={handlePercentageChange}
                />
              </S.PercentageSection>
            </>
          )}
          {!currentCharity && <div className="plg-search" data-partner-key={publicKey} />}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
